import { Component } from 'react';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';

/**
 * Error boundary wrapped around every routed page.
 *
 * The app had none. That is why the 2026-08-21 render loop presented as a
 * frozen tab with no message: React threw "Maximum update depth exceeded",
 * nothing caught it, the tree unmounted, and the router remounted it to
 * loop again — several times a second, aborting in-flight requests on each
 * pass. A boundary does not prevent the loop; it converts an invisible
 * freeze into a visible failure with a recovery path, which is the
 * difference between a five-minute diagnosis and an afternoon of one.
 *
 * Two recovery affordances:
 *   - "Try again" remounts the subtree without a full reload, which is
 *     enough for a transient failure.
 *   - "Reload the page" is the escape hatch for a corrupted client state
 *     the remount cannot clear.
 *
 * A failed dynamic import is called out separately. `withSuspense` gives
 * every route a Suspense fallback but no error path, so a chunk that 404s
 * after a redeploy — the client holding an index.html that references
 * hashed filenames the server has replaced — surfaces here. For that case
 * a reload genuinely is the fix, so it gets its own copy.
 */
const isChunkLoadError = (error) => {
  const message = String(error?.message ?? '');
  return (error?.name === 'ChunkLoadError' || /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(message));
};

export class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the component stack — it names the component that threw, which
    // for a render loop is the one to look at first.
    console.error('[RouteErrorBoundary]', error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const staleBuild = isChunkLoadError(error);

    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, p: 4 }}>
          <Stack spacing={2.5} sx={{
            alignItems: "flex-start"
          }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />

            <Typography variant="h6" sx={{
              fontWeight: 800
            }}>
              {staleBuild ? 'A newer version is available' : 'Something went wrong on this page'}
            </Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {staleBuild
                ? 'This page was updated while your tab was open. Reload to pick up the new version.'
                : "The page stopped responding and we've stopped it rather than leave it stuck. Your booking has not been charged."}
            </Typography>

            {import.meta.env.DEV && (
              <Box
                component="pre"
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  fontSize: 12,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  m: 0,
                }}
              >
                {String(error?.message ?? error)}
              </Box>
            )}

            <Stack direction="row" spacing={1.5}>
              {!staleBuild && (
                <Button variant="outlined" onClick={this.handleRetry}>
                  Try again
                </Button>
              )}
              <Button variant="contained" onClick={this.handleReload} sx={{ fontWeight: 600 }}>
                Reload the page
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    );
  }
}

export default RouteErrorBoundary;
